// fp-config.js
// Franchise Player — game rules and constants (frontend).
// Full rulebook: project doc "franchise-player-rules.md".
//
// League-specific numbers (captain budget, bench credits, shop prices, deadline)
// are calculated by the server when a league is created and stored on the
// league doc. The UI always shows the league's own frozen values; the numbers
// here are defaults and display copy.

export const FP_GAME_ID = "franchise-player";
export const FP_GAME_NAME = "Franchise Player";
export const FP_ROUTE = "/franchise-player";
export const FP_SEASON = 2026;

// ── Prices ───────────────────────────────────────────────────────────────────
// All prices are FanDuel weekly salaries (whole-week slate, Thursday-Monday).
export const PRICE_SOURCE_SITE = "FanDuel";

// How captain-shop prices are set (commissioner picks at league creation):
export const CAPTAIN_PRICING = {
  adp: {
    id: "adp",
    label: "Season-long rankings (ADP)",
    description: "Half-PPR average draft position, priced position by position on FanDuel's scale. Best for leagues starting before the season.",
  },
  current: {
    id: "current",
    label: "This week's FanDuel prices",
    description: "Each player's current FanDuel salary. Best for leagues starting mid-season.",
  },
};

// ── Franchise player (captain) ───────────────────────────────────────────────
export const CAPTAIN_SCORING_MULTIPLIER = 1.25; // franchise player scores 1.25x
export const MAX_CAPTAIN_KICKBACK = 2000;       // unspent captain dollars added to weekly budget, up to this
export const BENCH_CREDIT_PCT = 0.5;            // bench credit = 50% of the captain budget (rounded to $500)
export const IR_BENCH_CREDIT_PCT = 0.75;        // 75% while the franchise player is Out or on IR

// ── Weekly lineup ────────────────────────────────────────────────────────────
// 9 spots. The franchise player fills the spot for his position; the other 8
// (including a team defense) are bought each week.
export const WEEKLY_BUDGET = 50000;

export const LINEUP_SLOTS = [
  { id: "QB", label: "QB", eligiblePositions: ["QB"] },
  { id: "RB1", label: "RB", eligiblePositions: ["RB"] },
  { id: "RB2", label: "RB", eligiblePositions: ["RB"] },
  { id: "WR1", label: "WR", eligiblePositions: ["WR"] },
  { id: "WR2", label: "WR", eligiblePositions: ["WR"] },
  { id: "TE", label: "TE", eligiblePositions: ["TE"] },
  { id: "FLEX1", label: "FLEX", eligiblePositions: ["RB", "WR", "TE"] },
  { id: "FLEX2", label: "FLEX", eligiblePositions: ["RB", "WR", "TE"] },
  { id: "DEF", label: "D/ST", eligiblePositions: ["DEF"] },
];
export const BOUGHT_SPOTS = LINEUP_SLOTS.length - 1; // 8

export const PLAYER_POSITIONS = ["QB", "RB", "WR", "TE"];

// ── Entries ──────────────────────────────────────────────────────────────────
export const MAX_ENTRY_OPTIONS = [1, 2, 3, 5]; // commissioner picks one at league creation

// ── Scoring: FanDuel half PPR ────────────────────────────────────────────────
export const SCORING_RULES = {
  passYardsPerPoint: 25,   // 1 pt per 25 passing yards
  passTD: 4,
  interception: -1,
  rushYardsPerPoint: 10,   // 1 pt per 10 rushing yards
  rushTD: 6,
  reception: 0.5,          // half PPR
  recYardsPerPoint: 10,    // 1 pt per 10 receiving yards
  recTD: 6,
  fumbleLost: -2,
  twoPointConversion: 2,
  // No yardage bonuses.
};

// FanDuel D/ST scoring (confirm against FanDuel's current rules when scoring is built).
export const DST_SCORING = {
  sack: 1, interception: 2, fumbleRecovery: 2, touchdown: 6, safety: 2, blockedKick: 2,
  pointsAllowed: [[0, 10], [6, 7], [13, 4], [20, 1], [27, 0], [34, -1], [Infinity, -4]], // [max points allowed, fantasy points]
};

export const SCORING_FORMAT = "pool";        // total points vs the whole league, no head-to-head
export const SEASON_STANDINGS_BASIS = "points"; // cumulative points

// ── Injury status ────────────────────────────────────────────────────────────
// Long-term absences are hidden from the captain shop; short-term ones are
// shown with a warning tag. (Server applies the same rules when buying.)
export function injuryInfo(designation, returnDate) {
  const d = (designation || "").toLowerCase();
  if (!d) return null;
  const longTerm =
    d.includes("injured reserve") || d.includes("pup") || d.includes("suspend") ||
    d.includes("non-football") || daysUntil(returnDate) > 21;
  let short = "Q";
  if (d.includes("injured reserve")) short = "IR";
  else if (d.includes("out")) short = "OUT";
  else if (d.includes("doubtful")) short = "D";
  else if (d.includes("questionable")) short = "Q";
  else if (d.includes("suspend")) short = "SUSP";
  else if (d.includes("pup")) short = "PUP";
  return { short, longTerm, label: designation };
}

function daysUntil(yyyymmdd) {
  if (!yyyymmdd || String(yyyymmdd).length !== 8) return 0;
  const s = String(yyyymmdd);
  const t = Date.UTC(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8));
  return Math.round((t - Date.now()) / 86400000);
}

export function formatMoney(n) {
  return "$" + Math.round(n || 0).toLocaleString("en-US");
}
